import { registerDecorator, ValidationArguments, ValidationOptions } from "class-validator";
import format from "string-format";
import dayjs from "dayjs";
import { DATE_FORMAT, MIN_DAYJS } from "@/config/Common.ts";
import { ValidationEnum } from "@/enum/ValidationEnum.ts";
import { validatePhoneNumber } from "@/lib/utils.ts";
import { InspectionImageDto } from "@/dto/inspection/InspectionImageDto.ts";
import ConvertUtil from "@/util/ConvertUtil.ts";

type ImageFile = File & {
    base64?: string;
};

export function ValidateDate(name: string, required: boolean = true) {
    return (object: any, propertyName: string) => {
        if (required) {
            register(
                ValidationEnum.REQUIRED,
                object,
                propertyName,
                (value: string): boolean => {
                    return !!value;
                },
                { message: format("{name}을 입력해주세요", { name }) },
            );
        }

        register(
            ValidationEnum.MIN,
            object,
            propertyName,
            (value: string): boolean => {
                if (!required && !value) {
                    return true;
                }

                if (value === "Invalid Date") {
                    return true;
                }

                const date = dayjs(value, DATE_FORMAT);
                return date.isAfter(MIN_DAYJS);
            },
            { message: "1900년 1월 1일 이후로만 설정 가능합니다." },
        );
        register(
            ValidationEnum.REGEXP,
            object,
            propertyName,
            (value: string): boolean => {
                if (!required && !value) {
                    return true;
                }

                const date = dayjs(value);
                return date.isValid();
            },
            { message: "날짜 형태로 입력해주세요" },
        );
    };
}

export function ValidateText(name: string, required: ((obj: any) => boolean) | boolean = true) {
    return (object: any, propertyName: string) => {
        if (required) {
            register(
                ValidationEnum.REQUIRED,
                object,
                propertyName,
                (value: any, args: ValidationArguments): boolean => {
                    const isRequired = typeof required === "function" ? required(args.object) : required;
                    if (!isRequired) {
                        return true;
                    }

                    return !!value;
                },
                { message: format("{name}을 입력해주세요", { name }) },
            );
        }
    };
}

export function ValidateTextarea(name: string, required: boolean = true, maxLength: number) {
    return (object: any, propertyName: string) => {
        if (required) {
            register(
                ValidationEnum.REQUIRED,
                object,
                propertyName,
                (value: string): boolean => {
                    return !!value;
                },
                { message: format("{name}를 입력해주세요", { name }) },
            );
        }

        register(
            ValidationEnum.LENGTH,
            object,
            propertyName,
            (value: string): boolean => {
                if (!value) return true;
                return value.length <= maxLength;
            },
            { message: format("{name}은 {maxLength}자 이하로 입력해주세요", { name, maxLength }) },
        );
    };
}

export function ValidateContact(name: string, required: boolean = true) {
    return (object: any, propertyName: string) => {
        if (required) {
            register(
                ValidationEnum.REQUIRED,
                object,
                propertyName,
                (value: string): boolean => {
                    return !!value;
                },
                { message: format("{name}를 입력해주세요", { name }) },
            );
        }

        register(
            ValidationEnum.REGEXP,
            object,
            propertyName,
            (value: string): boolean => {
                if (!required && !value) {
                    return true;
                }

                return validatePhoneNumber(value);
            },
            { message: "올바른 번호 형식이 아니예요." },
        );
    };
}

export function ValidateRadio(
    name: string,
    required: ((obj: any) => boolean) | boolean = true,
    options: Array<{
        value: any;
    }> = [],
) {
    return (object: any, propertyName: string) => {
        register(
            ValidationEnum.ETC,
            object,
            propertyName,
            (value: any): boolean => {
                if (value === undefined || value === null) return true;

                // options에 있는 값이면 유효
                if (options.some(opt => opt.value === value)) return true;

                // options에 없는 값(custom input)인 경우
                // 빈 문자열이나 공백만 있는 경우 invalid
                return String(value).trim() !== "";
            },
            { message: format("{name}의 기타 항목을 입력해주세요", { name }) },
        );
        if (required) {
            register(
                ValidationEnum.REQUIRED,
                object,
                propertyName,
                (value: any, args: ValidationArguments): boolean => {
                    const isRequired = typeof required === "function" ? required(args.object) : required;
                    if (!isRequired) {
                        return true;
                    }
                    return value !== null && value !== undefined && value !== 0;
                },
                { message: format("{name}을 선택해주세요", { name }) },
            );
        }
    };
}

export function ValidateCheckbox(name: string, required: boolean = true) {
    return (object: any, propertyName: string) => {
        if (!required) return;

        register(
            ValidationEnum.REQUIRED,
            object,
            propertyName,
            (value: string[]): boolean => {
                return Array.isArray(value) && value.length > 0;
            },
            { message: format("{name}을 선택해주세요", { name }) },
        );

        register(
            ValidationEnum.ETC,
            object,
            propertyName,
            (value: string[]): boolean => {
                if (!value?.some(v => v === "ETC")) return true;

                const etcValue = value?.find(v => v.startsWith("ETC:"));
                if (!etcValue) return false;

                return etcValue.replace("ETC:", "").trim() !== "";
            },
            { message: format("{name}의 기타 항목을 력해주세요", { name }) },
        );
    };
}

export function ValidateSearch(name: string, required: boolean = true) {
    return (object: any, propertyName: string) => {
        if (required) {
            register(
                ValidationEnum.REQUIRED,
                object,
                propertyName,
                (value: string): boolean => {
                    return !!value;
                },
                { message: format("{name}를 입력해주세요", { name }) },
            );
        }
    };
}

export function ValidateNumber(name: string, required: ((obj: any) => boolean) | boolean = true) {
    return (object: any, propertyName: string) => {
        if (required) {
            register(
                ValidationEnum.REQUIRED,
                object,
                propertyName,
                (value: any, args: ValidationArguments): boolean => {
                    const isRequired = typeof required === "function" ? required(args.object) : required;
                    if (!isRequired) {
                        return true;
                    }

                    // 값이 0인 경우도 유효한 값으로 처리
                    return value !== undefined && value !== null && value !== "";
                },
                { message: format("{name}를 입력해주세요", { name }) },
            );
        }
    };
}

export function ValidateTime(name: string, required: boolean = true) {
    return (object: any, propertyName: string) => {
        if (required) {
            register(
                ValidationEnum.REQUIRED,
                object,
                propertyName,
                (value: string): boolean => {
                    return !!value;
                },
                { message: format("{name}을 입력해주세요", { name }) },
            );
        }
    };
}

export function ValidateTimeRange(startName: string, endName: string) {
    return (object: any, propertyName: string) => {
        register(
            ValidationEnum.RANGE,
            object,
            propertyName,
            (): boolean => {
                const startTime = object[startName];
                const endTime = object[endName];

                if (!startTime || !endTime) {
                    return true;
                }

                const [startHour, startMin] = startTime.split(":").map(Number);
                const [endHour, endMin] = endTime.split(":").map(Number);

                return startHour < endHour || (startHour === endHour && startMin < endMin);
            },
            { message: "종료 시간은 시작 시간 이후여야 해요" },
        );
    };
}

export function ValidateImageUpload(name: string, required: boolean = true, maxSize: number = 5) {
    return (object: any, propertyName: string) => {
        if (required) {
            register(
                ValidationEnum.REQUIRED,
                object,
                propertyName,
                (value: ImageFile, args: ValidationArguments): boolean => {
                    if (value) {
                        return true;
                    }
                    // @ts-ignore
                    const imageList: InspectionImageDto[] = args.object?.req?.inspectionImageList || [];
                    return imageList.some(item => item.category === args.property);
                },
                { message: format("{name}을/를 업로드해주세요", { name }) },
            );
        }

        register(
            ValidationEnum.MAX,
            object,
            propertyName,
            (value: ImageFile): boolean => {
                if (!value?.base64) return true;

                // base64 문자열의 크기 계산
                const base64Length = value.base64.length;
                const sizeInBytes = (base64Length * 3) / 4;
                const sizeInMB = sizeInBytes / (1024 * 1024);

                return sizeInMB <= maxSize;
            },
            { message: format("{name}의 크기는 {maxSize}MB 이하여야 해요.", { name, maxSize }) },
        );
    };
}

/**
 * 충전기별 이미지 필수 검증
 * @returns 에러가 있는 필드 목록 (빈 배열이면 검증 통과)
 */
export function validateChargerImages(
    chargerList: { chargerId: string }[],
    imageList: InspectionImageDto[],
    hasFile: (fieldName: string) => boolean,
    categories: { category: string; label: string }[],
): { fieldName: string; message: string }[] {
    const errors: { fieldName: string; message: string }[] = [];
    const existingImageKeys = new Set<string>();
    for (const img of imageList) {
        if (img.s3FileName) existingImageKeys.add(`${img.category}-${img.chargerId}`);
    }

    for (const charger of chargerList) {
        const chargerId = ConvertUtil.extractChargerId(charger.chargerId);

        for (const { category, label } of categories) {
            const fieldName = `${category}-${chargerId}`;
            const hasExisting = existingImageKeys.has(`${category}-${chargerId}`);

            if (!hasFile(fieldName) && !hasExisting) {
                errors.push({
                    fieldName,
                    message: `${label}을/를 업로드해주세요`,
                });
            }
        }
    }

    return errors;
}

export function ValidateLabel(name: string, required: ((obj: any) => boolean) | boolean = true) {
    return (object: any, propertyName: string) => {
        if (required) {
            register(
                ValidationEnum.REQUIRED,
                object,
                propertyName,
                (value: boolean, args): boolean => {
                    const isRequired = typeof required === "function" ? required(args.object) : required;
                    if (!isRequired) {
                        return true;
                    }
                    return value;
                },
                { message: format("{name}에 동의해주세요", { name }) },
            );
        }
    };
}

const register = (name: string, object: any, propertyName: string, validate: (value: any, args: ValidationArguments) => boolean, validationOptions?: ValidationOptions) => {
    registerDecorator({
        name,
        target: object.constructor,
        async: false,
        propertyName,
        constraints: [],
        options: validationOptions,
        validator: {
            validate,
        },
    });
};
